import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '@/lib/apiClient';

import {
  type Template,
  type TemplateDraft,
  type TemplateStatus,
  createTemplate,
  duplicateTemplate,
  getTemplate,
  listTemplates,
  saveTemplate,
  setTemplateStatus,
} from './api';

export const TEMPLATES_KEY = ['templates'] as const;

const retry = (failureCount: number, error: unknown) => {
  if (error instanceof ApiError && error.status < 500) return false;
  return failureCount < 2;
};

export function useTemplates(status: TemplateStatus) {
  return useQuery({
    queryKey: [...TEMPLATES_KEY, status],
    queryFn: () => listTemplates(status),
    retry,
  });
}

export function useTemplate(templateId: string) {
  return useQuery({
    queryKey: [...TEMPLATES_KEY, 'item', templateId],
    queryFn: () => getTemplate(templateId),
    retry,
    // O editor guarda o modelo em estado local enquanto a pessoa digita.
    // Um refetch em segundo plano trocaria o que está debaixo dos dedos dela.
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });
}

function useTemplateMutation<TVars>(fn: (vars: TVars) => Promise<Template>) {
  const queryClient = useQueryClient();
  return useMutation<Template, Error, TVars>({
    mutationFn: fn,
    onSuccess: (modelo) => {
      // A resposta traz a versão nova. Sem guardá-la, a gravação seguinte
      // mandaria a versão velha e levaria 409 — na própria tela que acabou de
      // salvar com sucesso.
      queryClient.setQueryData([...TEMPLATES_KEY, 'item', modelo.template_id], modelo);
      void queryClient.invalidateQueries({
        queryKey: TEMPLATES_KEY,
        predicate: (consulta) => consulta.queryKey[1] !== 'item',
      });
    },
  });
}

export function useCreateTemplate() {
  return useTemplateMutation((draft: TemplateDraft) => createTemplate(draft));
}

export function useSaveTemplate() {
  return useTemplateMutation(
    ({
      templateId,
      draft,
      version,
    }: {
      templateId: string;
      draft: TemplateDraft;
      version: number;
    }) => saveTemplate(templateId, draft, version),
  );
}

export function useSetTemplateStatus() {
  return useTemplateMutation(
    ({
      templateId,
      status,
      version,
    }: {
      templateId: string;
      status: TemplateStatus;
      version: number;
    }) => setTemplateStatus(templateId, status, version),
  );
}

export function useDuplicateTemplate() {
  return useTemplateMutation(({ templateId, title }: { templateId: string; title: string }) =>
    duplicateTemplate(templateId, title),
  );
}

/**
 * A versão que o servidor tem, quando ela discorda da nossa.
 *
 * Vem em `details.current_version` para a tela saber **quanto** ficou para
 * trás — recarregar às cegas depois de um 409 apagaria o que a pessoa acabou
 * de digitar sem ela entender por quê.
 */
export function conflictVersion(error: unknown): number | null {
  if (error instanceof ApiError && error.code === 'version_conflict') {
    const atual = error.details.current_version;
    return typeof atual === 'number' ? atual : null;
  }
  return null;
}

export function templateErrorKey(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.code === 'version_conflict') return 'templates:error.conflict';
    if (error.code === 'forbidden') return 'templates:error.forbidden';
    const rule = error.details.rule;
    if (typeof rule === 'string') return `templates:rule.${rule}`;
  }
  return 'templates:error.unknown';
}
